import React, { useEffect, useState } from 'react';
import teamService from '../../../services/teamService';
import { DEFAULT_TEAM_FORM } from '../constants/teamForm';
import ColorPaletteModal from './ColorPaletteModal';
import TeamOrderConstructor from './TeamOrderConstructor';

const TeamsManagement = ({ teamsData, onRefresh, onError }) => {
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [showColorPalette, setShowColorPalette] = useState(false);
  const [colorPaletteTarget, setColorPaletteTarget] = useState(null);
  const [newTeamData, setNewTeamData] = useState(DEFAULT_TEAM_FORM);
  const [orderedTeams, setOrderedTeams] = useState(teamsData);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [showOrderConstructor, setShowOrderConstructor] = useState(false);

  useEffect(() => {
    setOrderedTeams(teamsData);
  }, [teamsData]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && showColorPalette) {
        closeColorPalette();
      }
    };

    if (showColorPalette) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showColorPalette]);

  const openColorPalette = (target) => {
    setColorPaletteTarget(target);
    setShowColorPalette(true);
  };

  const closeColorPalette = () => {
    setShowColorPalette(false);
    setColorPaletteTarget(null);
  };

  const selectColorFromPalette = (color) => {
    if (colorPaletteTarget === 'new') {
      setNewTeamData({ ...newTeamData, color });
    } else if (colorPaletteTarget === 'edit' && editingTeam) {
      setEditingTeam({ ...editingTeam, color });
    }
    closeColorPalette();
  };

  const handleCreateTeam = async () => {
    try {
      const teamData = {
        ...newTeamData,
        members: newTeamData.members.split(',').map((member) => member.trim()).filter(Boolean),
        steps: [],
        stepsCount: 0,
      };

      await teamService.createTeam(teamData);
      setNewTeamData(DEFAULT_TEAM_FORM);
      setShowCreateTeam(false);
      onRefresh();
    } catch (error) {
      console.error('Ошибка создания команды:', error);
      onError('Ошибка создания команды');
    }
  };

  const handleEditTeam = (teamName) => {
    const team = orderedTeams.find((item) => item.name === teamName);
    if (team) {
      setEditingTeam({
        ...team,
        originalName: team.name,
        members: Array.isArray(team.members) ? team.members.join(', ') : team.members || '',
      });
    }
  };

  const handleUpdateTeam = async () => {
    try {
      const updatedTeam = {
        ...editingTeam,
        members: editingTeam.members.split(',').map((member) => member.trim()).filter(Boolean),
      };

      await teamService.updateTeam(editingTeam.originalName || editingTeam.name, updatedTeam);
      setEditingTeam(null);
      setTimeout(onRefresh, 100);
    } catch (error) {
      console.error('Ошибка обновления команды:', error);
      onError('Ошибка обновления команды');
    }
  };

  const handleDeleteTeam = async (teamName) => {
    if (!window.confirm(`Вы уверены, что хотите удалить команду "${teamName}"?`)) {
      return;
    }

    try {
      await teamService.deleteTeam(teamName);
      onRefresh();
    } catch (error) {
      console.error('Ошибка удаления команды:', error);
      onError('Ошибка удаления команды');
    }
  };

  const handleExportTeam = (teamName) => {
    const team = orderedTeams.find((item) => item.name === teamName);
    if (!team) {
      return;
    }

    const dataBlob = new Blob([JSON.stringify(team, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${teamName}_data.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const reorderList = (list, fromIndex, toIndex) => {
    const next = [...list];
    const [movedTeam] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, movedTeam);
    return next;
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const persistTeamOrder = async (nextTeams) => {
    const previousTeams = orderedTeams;
    setOrderedTeams(nextTeams);

    try {
      setIsReordering(true);
      await teamService.reorderTeams(nextTeams.map((team) => team.name));
      onRefresh();
      return true;
    } catch (error) {
      console.error('Ошибка изменения порядка команд:', error);
      setOrderedTeams(previousTeams);
      onError('Ошибка изменения порядка команд');
      return false;
    } finally {
      setIsReordering(false);
    }
  };

  const handleDrop = async (targetIndex) => {
    if (draggedIndex === null || draggedIndex === targetIndex || isReordering) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const nextTeams = reorderList(orderedTeams, draggedIndex, targetIndex);
    setDraggedIndex(null);
    setDragOverIndex(null);
    await persistTeamOrder(nextTeams);
  };

  const handleConstructorSave = async (nextTeams) => {
    const saved = await persistTeamOrder(nextTeams);
    if (saved) {
      setShowOrderConstructor(false);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="admin-content">
      <div className="teams-management">
        <div className="teams-header">
          <div>
            <h3>Управление командами</h3>
            <p className="teams-order-hint">
              Перетащите большую карточку за ⋮⋮ или откройте конструктор порядка для быстрой расстановки
            </p>
          </div>
          <div className="teams-actions">
            <button
              className="order-constructor-btn"
              onClick={() => setShowOrderConstructor(true)}
              type="button"
              disabled={orderedTeams.length < 2 || isReordering}
            >
              🔧 Конструктор порядка
            </button>
            <button className="create-team-btn" onClick={() => setShowCreateTeam(true)} type="button">
              ➕ Создать команду
            </button>
            <button className="refresh-teams-btn" onClick={onRefresh} type="button">
              🔄 Обновить данные
            </button>
          </div>
        </div>

        {showCreateTeam && (
          <div className="create-team-modal">
            <div className="modal-content">
              <h4>Создать новую команду</h4>
              <div className="form-group">
                <label>Название команды:</label>
                <input
                  type="text"
                  value={newTeamData.name}
                  onChange={(event) => setNewTeamData({ ...newTeamData, name: event.target.value })}
                  placeholder="Введите название команды"
                />
              </div>
              <div className="form-group">
                <label>Задание команды:</label>
                <textarea
                  value={newTeamData.description}
                  onChange={(event) =>
                    setNewTeamData({ ...newTeamData, description: event.target.value })
                  }
                  placeholder="Введите задание для команды..."
                />
              </div>
              <div className="form-group">
                <label>Отдел:</label>
                <input
                  type="text"
                  value={newTeamData.department}
                  onChange={(event) =>
                    setNewTeamData({ ...newTeamData, department: event.target.value })
                  }
                  placeholder="Название отдела"
                />
              </div>
              <div className="form-group">
                <label>Руководитель:</label>
                <input
                  type="text"
                  value={newTeamData.leader}
                  onChange={(event) => setNewTeamData({ ...newTeamData, leader: event.target.value })}
                  placeholder="ФИО руководителя"
                />
              </div>
              <div className="form-group">
                <label>Участники (через запятую):</label>
                <input
                  type="text"
                  value={newTeamData.members}
                  onChange={(event) => setNewTeamData({ ...newTeamData, members: event.target.value })}
                  placeholder="Иванов И.И., Петров П.П."
                />
              </div>
              <div className="form-group">
                <label>Статус:</label>
                <select
                  value={newTeamData.status}
                  onChange={(event) => setNewTeamData({ ...newTeamData, status: event.target.value })}
                >
                  <option value="active">Активная</option>
                  <option value="inactive">Неактивная</option>
                  <option value="archived">Архивная</option>
                </select>
              </div>
              <div className="form-group">
                <label>Цвет команды:</label>
                <div className="color-picker-container">
                  <input
                    type="color"
                    value={newTeamData.color}
                    onChange={(event) => setNewTeamData({ ...newTeamData, color: event.target.value })}
                    className="color-picker"
                    id="new-team-color-picker"
                  />
                  <span
                    className="color-preview"
                    style={{ backgroundColor: newTeamData.color }}
                    onClick={() => document.getElementById('new-team-color-picker').click()}
                    title="Кликните для выбора цвета"
                    role="button"
                    tabIndex={0}
                  />
                  <button
                    type="button"
                    className="quick-color-btn"
                    onClick={() => openColorPalette('new')}
                  >
                    🎨 Быстрый выбор
                  </button>
                </div>
              </div>
              <div className="modal-actions">
                <button onClick={handleCreateTeam} className="save-btn" type="button">
                  💾 Создать
                </button>
                <button onClick={() => setShowCreateTeam(false)} className="cancel-btn" type="button">
                  ❌ Отмена
                </button>
              </div>
            </div>
          </div>
        )}

        {editingTeam && (
          <div className="edit-team-modal">
            <div className="modal-content">
              <h4>Редактировать команду: {editingTeam.originalName || editingTeam.name}</h4>
              <div className="form-group">
                <label>Название команды:</label>
                <input
                  type="text"
                  value={editingTeam.name}
                  onChange={(event) => setEditingTeam({ ...editingTeam, name: event.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Задание команды:</label>
                <textarea
                  value={editingTeam.description || ''}
                  onChange={(event) =>
                    setEditingTeam({ ...editingTeam, description: event.target.value })
                  }
                  placeholder="Введите задание для команды..."
                />
              </div>
              <div className="form-group">
                <label>Отдел:</label>
                <input
                  type="text"
                  value={editingTeam.department || ''}
                  onChange={(event) =>
                    setEditingTeam({ ...editingTeam, department: event.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>Руководитель:</label>
                <input
                  type="text"
                  value={editingTeam.leader || ''}
                  onChange={(event) => setEditingTeam({ ...editingTeam, leader: event.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Участники (через запятую):</label>
                <input
                  type="text"
                  value={editingTeam.members}
                  onChange={(event) => setEditingTeam({ ...editingTeam, members: event.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Статус:</label>
                <select
                  value={editingTeam.status || 'active'}
                  onChange={(event) => setEditingTeam({ ...editingTeam, status: event.target.value })}
                >
                  <option value="active">Активная</option>
                  <option value="inactive">Неактивная</option>
                  <option value="archived">Архивная</option>
                </select>
              </div>
              <div className="form-group">
                <label>Цвет команды:</label>
                <div className="color-picker-container">
                  <input
                    type="color"
                    value={editingTeam.color || '#3bbddb'}
                    onChange={(event) => setEditingTeam({ ...editingTeam, color: event.target.value })}
                    className="color-picker"
                    id="edit-team-color-picker"
                  />
                  <span
                    className="color-preview"
                    style={{ backgroundColor: editingTeam.color || '#3bbddb' }}
                    onClick={() => document.getElementById('edit-team-color-picker').click()}
                    title="Кликните для выбора цвета"
                    role="button"
                    tabIndex={0}
                  />
                  <button
                    type="button"
                    className="quick-color-btn"
                    onClick={() => openColorPalette('edit')}
                  >
                    🎨 Быстрый выбор
                  </button>
                </div>
              </div>
              <div className="modal-actions">
                <button onClick={handleUpdateTeam} className="save-btn" type="button">
                  💾 Сохранить
                </button>
                <button onClick={() => setEditingTeam(null)} className="cancel-btn" type="button">
                  ❌ Отмена
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={`teams-list ${isReordering ? 'teams-list--reordering' : ''}`}>
          {orderedTeams.map((team, index) => (
            <div
              key={team.name}
              className={`team-management-card ${
                draggedIndex === index ? 'dragging' : ''
              } ${dragOverIndex === index ? 'drag-over' : ''}`}
              onDragOver={(event) => handleDragOver(event, index)}
              onDrop={(event) => {
                event.preventDefault();
                handleDrop(index);
              }}
              onDragLeave={() => setDragOverIndex(null)}
            >
              <div className="team-header">
                <div className="team-title-section">
                  <button
                    type="button"
                    className="team-drag-handle"
                    draggable={!isReordering}
                    onDragStart={() => handleDragStart(index)}
                    onDragEnd={handleDragEnd}
                    title="Перетащите для изменения порядка"
                    aria-label={`Изменить порядок команды ${team.name}`}
                  >
                    ⋮⋮
                  </button>
                  <div
                    className="team-color-indicator"
                    style={{ backgroundColor: team.color || '#3bbddb' }}
                  />
                  <h4>{team.name}</h4>
                </div>
                <div className={`status-badge ${team.status || 'active'}`}>
                  {team.status === 'active'
                    ? '🟢 Активная'
                    : team.status === 'inactive'
                      ? '🟡 Неактивная'
                      : '🔴 Архивная'}
                </div>
              </div>

              <div className="team-info">
                <p>
                  <strong>Описание:</strong> {team.description || 'Не указано'}
                </p>
                <p>
                  <strong>Отдел:</strong> {team.department || 'Не указан'}
                </p>
                <p>
                  <strong>Руководитель:</strong> {team.leader || 'Не указан'}
                </p>
                <p>
                  <strong>Участники:</strong>{' '}
                  {Array.isArray(team.members) ? team.members.join(', ') : team.members || 'Не указаны'}
                </p>
                <p>
                  <strong>Шагов:</strong> {team.stepsCount ?? team.steps?.length ?? 0}
                </p>
                <p>
                  <strong>Создана:</strong>{' '}
                  {team.createdAt ? new Date(team.createdAt).toLocaleDateString() : 'Не указана'}
                </p>
                <p>
                  <strong>Обновлена:</strong>{' '}
                  {team.updatedAt ? new Date(team.updatedAt).toLocaleDateString() : 'Не обновлялась'}
                </p>
              </div>

              <div className="team-actions">
                <button
                  type="button"
                  onClick={() => handleEditTeam(team.name)}
                  className="action-btn edit-btn"
                >
                  ✏️ Редактировать
                </button>
                <button
                  type="button"
                  onClick={() => handleExportTeam(team.name)}
                  className="action-btn export-btn"
                >
                  📤 Экспорт
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteTeam(team.name)}
                  className="action-btn delete-btn"
                >
                  🗑️ Удалить
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <ColorPaletteModal
        isOpen={showColorPalette}
        onClose={closeColorPalette}
        onSelectColor={selectColorFromPalette}
      />

      <TeamOrderConstructor
        isOpen={showOrderConstructor}
        teams={orderedTeams}
        onClose={() => setShowOrderConstructor(false)}
        onSave={handleConstructorSave}
        isSaving={isReordering}
      />
    </div>
  );
};

export default TeamsManagement;
